import React, { useContext } from 'react';
import numeral from 'numeral';
import { createColumnHelper } from '@tanstack/react-table';
import { Button, Link, Stack, Typography } from '@mui/material';

import { translate as t } from 'hooks/useTranslations';
import { GroupsContext } from '../context';
import { AppContext } from 'views/App/context';
import { ActionMenu } from 'components/_atoms';
import ListSection from 'components/_layouts/ListSection';
import CommonLayout from 'views/App/Portal/_layouts/CommonLayout';
import TruncatedText from '_lex/_atoms/TruncatedText';
import { Group } from 'views/App/Portal/LexID/Groups/_types/group';

const columnHelper = createColumnHelper<Group>();

const GroupsList: React.FC = () => {
  const { currentOrganizationId, hasPermission } = useContext(AppContext);
  const { groups, groupsQuery } = useContext(GroupsContext);

  const columns = [
    columnHelper.accessor('organizationGroupId', {
      cell: (info) => <Typography>{info.getValue()}</Typography>,
      header: t('groups.properties.organizationGroupId'),
      size: 2,
    }),
    columnHelper.accessor('name', {
      cell: (info) => (
        <Stack>
          <Link href={`./${info.row.original.id}`}>
            <TruncatedText
              text={info.row.original.name}
              maxWidth={420}
              typographyProps={{
                variant: 'h6',
                sx: { fontWeight: 'semibold' },
              }}
            />
          </Link>
          {info.row.original.description && <Typography variant='body2'>{info.row.original.description}</Typography>}
        </Stack>
      ),
      header: () => t('groups.properties.name'),
      size: 9999,
    }),
    columnHelper.accessor('totalUserCount', {
      cell: (info) => (
        <Typography>{(info.getValue() || 0) >= 0 ? numeral(info.getValue()).format('0,0') : '--'}</Typography>
      ),
      header: t('groups.properties.userCount'),
      size: 2,
    }),
    columnHelper.display({
      id: 'actions',
      cell: (info) =>
        currentOrganizationId && hasPermission('updateGroup', currentOrganizationId) ? (
          <ActionMenu
            id={`action_menu_${info.row.original.id}`}
            items={[
              {
                key: `edit_${info.row.original.id}`,
                label: t('common.edit'),
                href: `./edit/${info.row.original.id}`,
              },
            ]}
          />
        ) : null,
      header: () => '',
      size: 60,
    }),
  ];

  const createButton =
    currentOrganizationId && hasPermission('createGroup', currentOrganizationId) ? (
      <Button
        variant='contained'
        size='small'
        href='./new'
      >
        {t('groups.actions.create')}
      </Button>
    ) : null;

  return (
    <CommonLayout
      headerProps={{
        title: t('groups.title'),
        actions: createButton,
      }}
    >
      <Stack spacing={4}>
        <ListSection
          columns={columns}
          data={groups || []}
          query={groupsQuery}
          showChrome={true}
        />
      </Stack>
    </CommonLayout>
  );
};

export default GroupsList;
